/**
 * Operations Workflow panel for an applicant screening.
 *
 * Two roles, one component:
 *  • NTN staff: full editor — three section cards (Employment / Landlord /
 *    Documents), notes pane with private/shared toggle, activity timeline.
 *  • Property manager: progress milestones only + "Updates from NTN" shared
 *    notes feed. Private notes are never fetched (server-side enforced).
 *
 * Reuses existing apiClient + auth context. No new auth surface.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/dateFormat";
import { isPmVisibleNote } from "@/lib/verifyHub";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Briefcase, Home, FileText, CheckCircle2, Clock, Circle, XCircle,
  Lock, Eye, MessageSquare, Activity, Inbox, FileCheck, ListChecks,
} from "lucide-react";
import {
  PIPELINE_STAGES,
  PIPELINE_DOT,
  PIPELINE_FILL,
  PIPELINE_PILL,
  verifyStatusLabel,
} from "@/lib/verifyHub";

const SECTION_DEFS = [
  { key: "employment", label: "Employment Verification", icon: Briefcase },
  { key: "landlord",   label: "Landlord Verification",   icon: Home },
  { key: "documents",  label: "Document Review",         icon: FileText },
];

// Status pill vocabulary — supports both the canonical enum (Phase 2 canonical
// source at `verifications.<track>.canonical_status`) and the legacy Queue enum
// (rendered from the read-only `workflow.<track>.status` compatibility mirror).
// Every value listed here is a value that could legitimately appear on-screen.
const STATUS_OPTIONS = [
  // Canonical
  { value: "not_started",           label: "Not started",             icon: Circle,        color: "text-[#4A5568]" },
  { value: "in_progress",           label: "In progress",             icon: Clock,         color: "text-[#F59E0B]" },
  { value: "waiting_on_third_party", label: "Waiting on third party", icon: Clock,         color: "text-[#A16207]" },
  { value: "needs_review",          label: "Needs review",            icon: Clock,         color: "text-[#F59E0B]" },
  { value: "completed",             label: "Completed",               icon: CheckCircle2,  color: "text-[#15803D]" },
  { value: "unable_to_verify",      label: "Unable to verify",        icon: XCircle,       color: "text-[#EF4444]" },
  { value: "cancelled",             label: "Cancelled",               icon: XCircle,       color: "text-[#4A5568]" },
  // Legacy (read-only compatibility mirror)
  { value: "follow_up_required",    label: "Follow-up required",      icon: Clock,         color: "text-[#A16207]" },
  { value: "verified",              label: "Verified",                icon: CheckCircle2,  color: "text-[#22C55E]" },
  { value: "complete",              label: "Complete",                icon: CheckCircle2,  color: "text-[#15803D]" },
  { value: "pending_review",        label: "Pending review",          icon: Circle,        color: "text-[#4A5568]" },
  { value: "in_review",             label: "In review",               icon: Clock,         color: "text-[#F59E0B]" },
  { value: "additional_documents_needed", label: "Additional docs needed", icon: Clock,   color: "text-[#A16207]" },
];

const STATUS_BY_VALUE = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));

// Re-export from the shared verifyHub lib so the same stages drive the
// public landing page, the dashboard pipeline, and this PM milestone tracker.
const MILESTONES = PIPELINE_STAGES.map((s) => ({
  key: s.verifyStatus,
  label: s.label,
  icon: s.icon,
  tone: s.tone,
}));

function StatusPill({ status }) {
  const s = STATUS_BY_VALUE[status] || STATUS_BY_VALUE.not_started;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-overline tracking-wider uppercase ${s.color}`} data-testid={`status-pill-${status}`}>
      <Icon className="h-3.5 w-3.5" />
      {s.label}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  return fmtDateTime(iso, "");
}

// ---------- Section card (read-only after Phase-2) ----------
// Status is now edited exclusively in the Operations workspace. This card
// surfaces the canonical status from `verifications.<track>.canonical_status`
// (mirrored transactionally into `workflow.<track>` for backward compat)
// and points staff to the Operations write path.
function SectionCard({ section, value, sid }) {
  const Icon = section.icon;
  return (
    <div className="border border-[#0A192F]/10 bg-white p-5 rounded-sm space-y-4" data-testid={`workflow-section-${section.key}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <Icon className="h-4 w-4 text-[#0A192F]" />
          <div className="font-serif text-base text-[#0A192F]">{section.label}</div>
        </div>
        <StatusPill status={value.canonical_status || value.status} />
      </div>

      {value.summary ? (
        <div
          className="text-sm text-[#0A192F]/85 whitespace-pre-wrap p-3 rounded-sm bg-[#F3EFEA]/30 border border-[#0A192F]/10"
          data-testid={`workflow-summary-${section.key}`}
        >
          {value.summary}
        </div>
      ) : (
        <div className="text-sm text-[#4A5568] italic" data-testid={`workflow-summary-empty-${section.key}`}>
          No summary yet. Add one from Operations.
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] text-[#4A5568]">
          {value.updated_at ? (
            <>Updated {fmtTime(value.updated_at)}{value.updated_by_name ? ` · ${value.updated_by_name}` : ""}</>
          ) : "Never updated"}
        </div>
        <a
          href={`/staff-ops/${section.key}/${sid}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A192F] hover:underline"
          data-testid={`workflow-open-ops-${section.key}`}
        >
          Update in Operations →
        </a>
      </div>
    </div>
  );
}

// ---------- Notes pane (NTN editor) ----------
function NotesPane({ sid, notes, reload }) {
  const [content, setContent] = useState("");
  const [visibility, setVisibility] = useState("staff_only");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!content.trim()) return;
    setBusy(true);
    try {
      await apiClient.post(`/screenings/${sid}/workflow/notes`, { visibility, content });
      setContent("");
      toast.success(visibility === "property_manager" ? "Shared update posted" : "Internal note saved");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save note");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#0A192F]/10 bg-white p-5 rounded-sm" data-testid="workflow-notes-pane">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="h-4 w-4 text-[#0A192F]" />
        <div className="font-serif text-base text-[#0A192F]">Case Notes</div>
      </div>
      <p className="text-[11px] text-[#4A5568] mb-3">
        Private notes stay within NTN and are never shown to the subscriber. Shared notes
        appear in the property manager&apos;s &ldquo;Updates from NTN&rdquo; feed.
      </p>

      <div className="space-y-2">
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Add a note…"
          rows={3}
          maxLength={4000}
          className="text-sm rounded-sm border-[#0A192F]/20"
          data-testid="workflow-note-input"
        />
        <div className="flex items-center justify-between gap-3">
          <div className="inline-flex items-center rounded-sm border border-[#0A192F]/20 text-xs overflow-hidden">
            <button
              type="button"
              onClick={() => setVisibility("staff_only")}
              className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${visibility === "staff_only" ? "bg-[#0A192F] text-white" : "bg-white text-[#0A192F]"}`}
              data-testid="workflow-note-visibility-private"
            >
              <Lock className="h-3 w-3" /> Internal (NTN only)
            </button>
            <button
              type="button"
              onClick={() => setVisibility("property_manager")}
              className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${visibility === "property_manager" ? "bg-[#0A192F] text-white" : "bg-white text-[#0A192F]"}`}
              data-testid="workflow-note-visibility-shared"
            >
              <Eye className="h-3 w-3" /> Shared with PM
            </button>
          </div>
          <Button
            size="sm"
            disabled={!content.trim() || busy}
            onClick={submit}
            className="rounded-sm bg-[#0A192F] hover:bg-[#0A192F]/90 text-white px-4"
            data-testid="workflow-note-submit"
          >
            {busy ? "Posting…" : "Post note"}
          </Button>
        </div>
      </div>

      <div className="mt-5 space-y-3 max-h-[420px] overflow-y-auto pr-1">
        {notes.length === 0 && <div className="text-xs text-[#4A5568] italic">No notes yet.</div>}
        {notes.map((n) => (
          <div
            key={n.id}
            className={`p-3 rounded-sm border ${isPmVisibleNote(n) ? "border-[#22C55E]/30 bg-[#22C55E]/5" : "border-[#0A192F]/15 bg-[#F3EFEA]/40"}`}
            data-testid={`workflow-note-${n.id}`}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 text-[10px] font-overline uppercase tracking-wider ${isPmVisibleNote(n) ? "text-[#15803D]" : "text-[#4A5568]"}`}>
                {isPmVisibleNote(n) ? <Eye className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                {isPmVisibleNote(n) ? "Shared with PM" : "Private (NTN only)"}
              </span>
              <span className="text-[10px] text-[#4A5568]">{fmtTime(n.created_at)}</span>
            </div>
            <div className="text-sm text-[#0A192F] whitespace-pre-wrap break-words">{n.content}</div>
            <div className="text-[10px] text-[#4A5568] mt-1">— {n.author_name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Activity Timeline ----------
function ActivityTimeline({ items }) {
  return (
    <div className="border border-[#0A192F]/10 bg-white p-5 rounded-sm" data-testid="workflow-activity-timeline">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-[#0A192F]" />
        <div className="font-serif text-base text-[#0A192F]">Activity Timeline</div>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#4A5568] italic">No activity yet.</div>
      ) : (
        <ol className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {items.map((it) => (
            <li key={it.id} className="flex gap-3" data-testid={`workflow-activity-${it.id}`}>
              <div className="flex flex-col items-center pt-1">
                <div className={`h-2 w-2 rounded-full ${it.visibility === "shared" ? "bg-[#22C55E]" : "bg-[#0A192F]/40"}`} />
                <div className="flex-1 w-px bg-[#0A192F]/10 my-1" />
              </div>
              <div className="flex-1 min-w-0 pb-2">
                <div className="text-sm text-[#0A192F]">{it.message}</div>
                <div className="text-[10px] text-[#4A5568] mt-0.5">
                  {fmtTime(it.created_at)} · {it.actor_name}
                  {it.visibility === "shared" && <span className="ml-2 text-[#15803D] uppercase tracking-wider">Shared</span>}
                  {it.visibility === "internal" && <span className="ml-2 text-[#4A5568] uppercase tracking-wider">Private</span>}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------- PM Progress View ----------
function ProgressView({ progress, sharedNotes, verifyStatus }) {
  return (
    <div className="space-y-6" data-testid="workflow-pm-progress">
      <div className="bg-white rounded-xl ring-1 ring-slate-200 shadow-sm overflow-hidden">
        <header className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">
              Verification Progress
            </div>
            <div className="font-serif text-base text-slate-900 mt-0.5">Six-stage workflow</div>
          </div>
          {verifyStatus && (
            <span
              className={`inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-semibold px-2.5 py-1 rounded-full ${
                PIPELINE_PILL[
                  (PIPELINE_STAGES.find((s) => s.verifyStatus === verifyStatus) || {}).tone
                ] || PIPELINE_PILL.neutral
              }`}
              data-testid="pm-verify-status"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  PIPELINE_DOT[
                    (PIPELINE_STAGES.find((s) => s.verifyStatus === verifyStatus) || {}).tone
                  ] || PIPELINE_DOT.neutral
                }`}
              />
              {verifyStatusLabel(verifyStatus)}
            </span>
          )}
        </header>

        <ol className="p-5 space-y-3">
          {MILESTONES.map((m, idx) => {
            const ms = progress?.[m.key] || { done: false, active: false, at: null };
            const Icon = m.icon;
            const isActive = !!ms.active;
            const isDone = !!ms.done;
            // Active stages adopt the milestone's own tone (blue/violet/amber)
            // matching the landing page; done stages use emerald like complete.
            const activeDotCls = `${PIPELINE_DOT[m.tone]} text-white animate-pulse`;
            const doneDotCls = "bg-emerald-500 text-white";
            const idleDotCls = "bg-slate-100 text-slate-500";
            const dotCls = isActive ? activeDotCls : isDone ? doneDotCls : idleDotCls;
            return (
              <li key={m.key} className="flex items-center gap-3" data-testid={`pm-milestone-${m.key}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${dotCls}`}>
                  {!isActive && isDone ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${isDone || isActive ? "text-slate-900 font-medium" : "text-slate-500"}`}>
                    {idx + 1}. {m.label}
                    {isActive && (
                      <span
                        className={`ml-2 inline-flex items-center text-[10px] font-semibold tracking-widest uppercase px-2 py-0.5 rounded-full ${PIPELINE_PILL[m.tone]}`}
                      >
                        In progress
                      </span>
                    )}
                  </div>
                  {ms.at && (
                    <div className="text-[10px] text-slate-500 mt-0.5">{fmtTime(ms.at)}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="bg-white rounded-xl ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid="pm-shared-updates">
        <header className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">
              Updates from NTN
            </div>
            <div className="font-serif text-base text-slate-900 mt-0.5">Real-time visibility</div>
          </div>
        </header>
        <div className="p-5">
          {sharedNotes.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No updates yet. Your NTN team will post visibility here as the review progresses.</div>
          ) : (
            <ul className="space-y-3">
              {sharedNotes.map((n) => (
                <li key={n.id} className="border-l-2 border-emerald-500 pl-3" data-testid={`pm-shared-note-${n.id}`}>
                  <div className="text-sm text-slate-900 whitespace-pre-wrap break-words">{n.content}</div>
                  <div className="text-[10px] text-slate-500 mt-1">{fmtTime(n.created_at)} · {n.author_name}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Main Component ----------
export default function WorkflowPanel({ sid }) {
  const { user } = useAuth();
  const isStaff = user?.role === "ntn_staff";

  const [wf, setWf] = useState(null);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        apiClient.get(`/screenings/${sid}/workflow`),
        apiClient.get(`/screenings/${sid}/workflow/notes`),
        apiClient.get(`/screenings/${sid}/workflow/activity`),
      ]);
      setWf(a.data);
      setNotes(b.data || []);
      setActivity(c.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load workflow");
    } finally { setLoading(false); }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  const sharedNotes = useMemo(() => notes.filter(isPmVisibleNote), [notes]);

  if (loading || !wf) {
    return <div className="font-overline text-[#4A5568]" data-testid="workflow-loading">Loading workflow…</div>;
  }

  // ---- PM view ----
  if (!isStaff) {
    return <ProgressView progress={wf.progress} sharedNotes={sharedNotes} verifyStatus={wf.verify_status} />;
  }

  // ---- NTN staff view (read-only status; edits happen in Operations) ----
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="workflow-sections-grid">
        {SECTION_DEFS.map((sec) => (
          <SectionCard key={sec.key} section={sec} value={wf.sections[sec.key]} sid={sid} />
        ))}
      </div>

      <div className="border border-[#0A192F]/10 bg-[#F3EFEA]/30 p-4 rounded-sm" data-testid="workflow-progress-summary">
        <div className="font-overline text-[10px] text-[#4A5568] tracking-wider uppercase mb-2">PM Progress (what the property manager sees)</div>
        <div className="flex flex-wrap gap-2">
          {MILESTONES.map((m) => {
            const ms = wf.progress?.[m.key] || {};
            return (
              <span
                key={m.key}
                className={`text-[11px] px-2.5 py-1 rounded-sm border ${ms.done ? "bg-[#22C55E]/10 border-[#22C55E]/40 text-[#15803D]" : "bg-white border-[#0A192F]/15 text-[#4A5568]"}`}
              >
                {m.label}{ms.done && " ✓"}
              </span>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <NotesPane sid={sid} notes={notes} reload={load} />
        <ActivityTimeline items={activity} />
      </div>
    </div>
  );
}
